import type { ApiWire } from '@/domain/shared/types';

import type {
    ClientFishGroupsOverview,
    ClientFishGroupsOverviewDto,
    OverviewCage,
    OverviewCageDto,
    OverviewFishGroupSummary,
    OverviewFishGroupSummaryDto,
    OverviewLocation,
    OverviewLocationDto,
} from '../types';

import { asOverviewFishGroupStatus } from './enumGuards';

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export function mapClientFishGroupsOverviewToDomain(
    dto: ClientFishGroupsOverviewDto
): ClientFishGroupsOverview {
    return {
        clientId: dto.clientId,
        activeLocations: dto.activeLocations.map(mapOverviewLocationToDomain),
        inactiveLocations: dto.inactiveLocations.map(mapOverviewLocationToDomain),
    };
}

function mapOverviewLocationToDomain(location: OverviewLocationDto): OverviewLocation {
    const activeCages = location.activeCages.map(mapOverviewCageToDomain);
    const inactiveCages = location.inactiveCages.map(mapOverviewCageToDomain);
    return {
        locationId: location.locationId,
        locationName: location.locationName,
        activeCages,
        inactiveCages,
        cages: [...activeCages, ...inactiveCages],
    };
}

function mapOverviewCageToDomain(cage: OverviewCageDto): OverviewCage {
    return {
        cageId: cage.cageId,
        cageName: cage.cageName,
        activeBioscopes: cage.activeBioscopes ?? [],
        activeFishGroup: cage.activeFishGroup
            ? mapOverviewFishGroupSummaryToDomain(cage.activeFishGroup)
            : null,
    };
}

/**
 * Exported so the client-overview domain can reuse it: the same cohort summary
 * now rides on both the internal fish-groups overview and the customer-facing
 * region/location overview, and two copies would drift.
 */
export function mapOverviewFishGroupSummaryToDomain(
    fg: ApiWire<OverviewFishGroupSummaryDto>
): OverviewFishGroupSummary {
    return {
        id: fg.id,
        name: fg.name,
        status: asOverviewFishGroupStatus(fg.status),
        lifecycleStartedAt: fg.lifecycleStartedAt ?? null,
        fromDate: fg.fromDate,
    };
}
